import type {
  CompleteJobRequest,
  CreateJobRequest,
  CreateJobResponse,
  JobRecord,
} from './core/types';

const BASE_URL: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const message =
      (data && typeof data === 'object' && 'error' in data
        ? String((data as { error: unknown }).error)
        : String(data || `请求失败 (${res.status})`)) || `请求失败 (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return data as T;
}

export const api = {
  async createJob(req: CreateJobRequest): Promise<CreateJobResponse> {
    return request<CreateJobResponse>('/api/jobs', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  async uploadSource(uploadUrl: string, body: Blob | Uint8Array): Promise<void> {
    const res = await fetch(`${BASE_URL}${uploadUrl}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/pdf' },
      body: body as unknown as BodyInit,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new ApiError(text || `上传失败 (${res.status})`, res.status);
    }
  },

  /** Upload with progress callbacks (0..1) using XHR. */
  uploadSourceWithProgress(
    uploadUrl: string,
    body: Blob | Uint8Array,
    onProgress: (ratio: number) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', `${BASE_URL}${uploadUrl}`);
      xhr.setRequestHeader('Content-Type', 'application/pdf');
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(1);
          resolve();
        } else {
          reject(new ApiError(xhr.responseText || `上传失败 (${xhr.status})`, xhr.status));
        }
      };
      xhr.onerror = () => reject(new ApiError('网络错误，上传失败', 0));
      xhr.send(body as unknown as XMLHttpRequestBodyInit);
    });
  },

  async startJob(jobId: string): Promise<JobRecord> {
    return request<JobRecord>(`/api/jobs/${jobId}/start`, { method: 'POST' });
  },

  async getJob(jobId: string): Promise<JobRecord> {
    return request<JobRecord>(`/api/jobs/${jobId}`);
  },

  async completeJob(jobId: string, req: CompleteJobRequest): Promise<JobRecord> {
    return request<JobRecord>(`/api/jobs/${jobId}/complete`, {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  async failJob(jobId: string, error: string): Promise<JobRecord> {
    return request<JobRecord>(`/api/jobs/${jobId}/fail`, {
      method: 'POST',
      body: JSON.stringify({ error }),
    });
  },

  async getDownloadTickets(
    jobId: string,
    files: string[],
  ): Promise<Array<{ fileName: string; url: string; expiresAt: string }>> {
    const res = await request<{ tickets: Array<{ fileName: string; url: string; expiresAt: string }> }>(
      `/api/jobs/${jobId}/download-tickets`,
      { method: 'POST', body: JSON.stringify({ files }) },
    );
    return res.tickets;
  },

  async getOutputUploadTickets(
    jobId: string,
    files: string[],
  ): Promise<Array<{ fileName: string; url: string; expiresAt: string }>> {
    const res = await request<{ tickets: Array<{ fileName: string; url: string; expiresAt: string }> }>(
      `/api/jobs/${jobId}/output-tickets`,
      { method: 'POST', body: JSON.stringify({ files }) },
    );
    return res.tickets;
  },

  async uploadOutput(
    url: string,
    bytes: Uint8Array,
    onProgress?: (ratio: number) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', `${BASE_URL}${url}`);
      xhr.setRequestHeader('Content-Type', 'application/pdf');
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress?.(e.loaded / e.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(1);
          resolve();
        } else {
          reject(new ApiError(xhr.responseText || `上传失败 (${xhr.status})`, xhr.status));
        }
      };
      xhr.onerror = () => reject(new ApiError('网络错误，输出上传失败', 0));
      xhr.send(bytes as unknown as XMLHttpRequestBodyInit);
    });
  },
};

export { BASE_URL };
